import { useApolloClient } from "@apollo/client";
import { Button, Flex, Heading, Link, Spacer, Text } from "@chakra-ui/react";
import { useFlags } from "flagsmith/react";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback, useState } from "react";
import { NavLink, Link as RouterLink, useNavigate } from "react-router-dom";
import { Loading } from "src/components/Feedback/Loading";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import * as FSQ from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import {
  TeamMemberFormSchema,
  TeamMemberFormType,
  createEmptyTeamMember,
  isSchoolAdminVariaty,
  toAccessPermission,
} from "src/schemas/TeamMember";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import { isNonEmptyString } from "src/services/predicates";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { toTuple3 } from "src/types/remoteData";
import { TeamMemberForm } from "../components/Forms/TeamMemberForm";
import {
  GET_AVAILABLE_TEAMS,
  GET_MEMBERS,
  GET_SCHOOLS_BY_ORG_ID,
  GET_USER_GROUPS,
} from "./graphql/queries";

export const NewTeamMember = () => {
  const toast = useAvelaToast();
  const navigate = useNavigate();
  const flags = useFlags(["current-applying-schools"]);
  const [submitting, setSubmitting] = useState(false);
  const organization = useOrganization();

  const { remoteData: schoolData } = useRemoteDataQuery<
    GQL.GetSchoolsByOrgID,
    GQL.GetSchoolsByOrgIDVariables
  >(GET_SCHOOLS_BY_ORG_ID, {
    variables: {
      organizationID: organization.map((org) => org.id).withDefault(""),
    },
    fetchPolicy: "no-cache",
  });

  const { remoteData: userGroupData } = useRemoteDataQuery<GQL.GetUserGroups>(
    GET_USER_GROUPS,
    { fetchPolicy: "no-cache" }
  );

  const { remoteData: availableTeams } = useRemoteDataQuery<
    GQL.GetAvailableTeams,
    GQL.GetAvailableTeamsVariables
  >(GET_AVAILABLE_TEAMS, {
    variables: {
      organizationId: organization.map((org) => org.id).withDefault(""),
    },
    fetchPolicy: "cache-and-network",
  });

  const remoteData = toTuple3(schoolData, userGroupData, availableTeams);

  const apolloClient = useApolloClient();
  const handleSubmit = useCallback(
    async (
      value: TeamMemberFormType,
      helper: FormikHelpers<TeamMemberFormType>
    ) => {
      setSubmitting(true);
      try {
        if (!organization.hasData()) throw new Error("Invalid organization.");
        if (!userGroupData.hasData())
          throw new Error("Missing user group data");

        const selectedUserGroup = userGroupData.data.user_group.find(
          (ug) => ug.id === value.user_group_id
        );
        if (!selectedUserGroup) throw new Error("no selected user group");

        const school_permissions = toAccessPermission(
          value,
          selectedUserGroup.person_type
        );

        const schoolIds = isSchoolAdminVariaty(selectedUserGroup)
          ? value.selected_school_ids
          : [];

        await FSQ.createTeamMember({
          body: {
            firstName: value.first_name,
            lastName: value.last_name,
            emailAddress: value.email_address,
            organizationId: organization.data.id,
            schoolIds,
            userGroupId: value.user_group_id,
            schoolAccessPermission: school_permissions,
            teamId: isNonEmptyString(value.person_team_id)
              ? value.person_team_id
              : undefined,
          },
        });
        await apolloClient.refetchQueries({
          include: [GET_MEMBERS],
        });

        toast({
          title: "Team member created",
          isClosable: true,
          status: "info",
        });

        navigate(Url.OrgAdmin.Team.index(organization.data));
      } catch (err) {
        console.error(err);
        if ((err as any)?.error?.httpStatus === 409) {
          const personId = (err as any).error?.data?.personIds?.[0];
          toast({
            title: `Something went wrong!`,
            description: (
              <Text>
                A{" "}
                {isNonEmptyString(personId) && organization.hasData() ? (
                  <Link
                    as={RouterLink}
                    to={Url.OrgAdmin.Team.editMember(
                      organization.data,
                      personId
                    )}
                    textDecor="underline"
                    onClick={() => toast.closeAll()}
                  >
                    team member
                  </Link>
                ) : (
                  "team member"
                )}{" "}
                with this email address already exists
              </Text>
            ),
            isClosable: true,
            status: "error",
          });
          return "User already exists.";
        }
        const description = (): string => {
          if (err instanceof Error) {
            if (err.message.toLowerCase().includes("unauthorized")) {
              return "Not authorized to create team member.";
            }
          }
          return "Check your network and try again.";
        };
        toast({
          title: `Something went wrong!`,
          description: description(),
          isClosable: true,
          status: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [organization, userGroupData, apolloClient, toast, navigate]
  );

  const initialValues = createEmptyTeamMember(
    flags["current-applying-schools"].enabled
      ? "school:attending"
      : "school:applying"
  );

  if (remoteData.isLoading()) {
    return <Loading></Loading>;
  }
  return (
    <GQLRemoteDataView remoteData={remoteData}>
      {([schoolData, userGroupData, teamsData]) => {
        return (
          <Flex direction="column" height="100%">
            <Breadcrumb
              items={breadcrumb.team.getBreadcrumbsForNewMember(organization)}
              mb={8}
            />

            <Formik<TeamMemberFormType>
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validate={validateWithZod(TeamMemberFormSchema)}
            >
              {(formik) => {
                return (
                  <Flex
                    as={Form}
                    direction="column"
                    gap={6}
                    flexGrow="1"
                    noValidate
                  >
                    <Heading as="h1" fontSize="2xl" fontWeight="600">
                      Create Team Member
                    </Heading>
                    <TeamMemberForm
                      allSchools={schoolData.school}
                      userGroups={userGroupData.user_group}
                      teams={teamsData.team}
                    />
                    <Spacer />
                    <AdminFormButtons>
                      <Button
                        as={NavLink}
                        to={organization
                          .map((org) => Url.OrgAdmin.Team.index(org))
                          .withDefault("#")}
                        isLoading={organization.isLoading()}
                        variant="outline"
                        colorScheme="gray"
                      >
                        Cancel
                      </Button>
                      <Spacer />
                      <Button type="submit" isLoading={submitting}>
                        Create
                      </Button>
                    </AdminFormButtons>
                  </Flex>
                );
              }}
            </Formik>
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
};
